import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../config/axiosConfig';
import {
  getErrorMessage,
  normalizeDelivery,
  normalizeDeliveryListResponse,
  normalizeTrackingResponse,
} from '../../utils/apiResponseUtils';

// Async thunk for creating delivery
export const createDelivery = createAsyncThunk(
  'delivery/createDelivery',
  async (deliveryData, { rejectWithValue }) => {
    try {
      const payload = { ...deliveryData };
      if (['CASH', 'COD', 'CASH_ON_DELIVERY'].includes(payload.paymentMethod)) { payload.paymentMethod = 'CASH'; delete payload.paymentId; delete payload.orderId; }
      const response = await axiosInstance.post('/delivery/create', payload);
      return normalizeDelivery(response.data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Delivery creation failed'));
    }
  }
);

// Async thunk for fetching user deliveries
export const fetchUserDeliveries = createAsyncThunk(
  'delivery/fetchUserDeliveries',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/delivery/user-deliveries', { timeout: 30000 });
      return normalizeDeliveryListResponse(response.data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch deliveries'));
    }
  },
  { condition: (_, { getState }) => !getState().delivery.ordersRequestId }
);

// Async thunk for fetching delivery details
export const fetchDeliveryDetails = createAsyncThunk(
  'delivery/fetchDeliveryDetails',
  async (deliveryId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/delivery/${deliveryId}`);
      return normalizeDelivery(response.data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch delivery details'));
    }
  }
);

// Async thunk for tracking delivery
export const trackDelivery = createAsyncThunk(
  'delivery/trackDelivery',
  async (deliveryId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/delivery/track/${deliveryId}`);
      return normalizeTrackingResponse(response.data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Tracking failed'));
    }
  }
);

const initialState = {
  deliveries: [],
  ordersRequestId: null,
  ordersLoading: false,
  ordersError: null,
  currentDelivery: null,
  trackingData: null,
  loading: false,
  error: null,
  pickupLocation: null,
  dropLocation: null,
  selectedVehicle: null,
  senderContact: null,
  receiverContact: null,
  parcelDetails: {
    parcelSize: '',
    parcelWeight: '',
    parcelDescription: '',
  },
  estimatedCost: 0,
};

const deliverySlice = createSlice({
  name: 'delivery',
  initialState,
  reducers: {
    setPickupLocation: (state, action) => {
      state.pickupLocation = action.payload;
    },
    setDropLocation: (state, action) => {
      state.dropLocation = action.payload;
    },
    setSelectedVehicle: (state, action) => {
      state.selectedVehicle = action.payload;
    },
    setSenderContact: (state, action) => {
      state.senderContact = action.payload;
    },
    setReceiverContact: (state, action) => {
      state.receiverContact = action.payload;
    },
    setParcelDetails: (state, action) => {
      state.parcelDetails = {
        ...state.parcelDetails,
        ...action.payload,
      };
    },
    setEstimatedCost: (state, action) => {
      state.estimatedCost = Number(action.payload) || 0;
    },
    resetDeliveryForm: (state) => {
      state.pickupLocation = null;
      state.dropLocation = null;
      state.selectedVehicle = null;
      state.senderContact = null;
      state.receiverContact = null;
      state.parcelDetails = {
        parcelSize: '',
        parcelWeight: '',
        parcelDescription: '',
      };
      state.estimatedCost = 0;
      state.currentDelivery = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create delivery cases
      .addCase(createDelivery.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDelivery.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDelivery = action.payload;
        state.deliveries.push(action.payload);
      })
      .addCase(createDelivery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch deliveries cases
      .addCase(fetchUserDeliveries.pending, (state, action) => {
        state.ordersRequestId = action.meta.requestId;
        state.ordersLoading = true;
      })
      .addCase(fetchUserDeliveries.fulfilled, (state, action) => {
        if (state.ordersRequestId !== action.meta.requestId) return;
        state.ordersRequestId = null;
        state.ordersLoading = false;
        state.ordersError = null;
        state.deliveries = action.payload;
      })
      .addCase(fetchUserDeliveries.rejected, (state, action) => {
        if (state.ordersRequestId !== action.meta.requestId) return;
        state.ordersRequestId = null;
        state.ordersLoading = false;
        if (!action.meta.aborted) state.ordersError = action.payload || action.error.message;
      })
      // Fetch delivery details cases
      .addCase(fetchDeliveryDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeliveryDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDelivery = action.payload;

        const existingDeliveryIndex = state.deliveries.findIndex(
          (delivery) => delivery.id === action.payload.id
        );

        if (existingDeliveryIndex >= 0) {
          state.deliveries[existingDeliveryIndex] = action.payload;
        }
      })
      .addCase(fetchDeliveryDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Track delivery cases
      .addCase(trackDelivery.pending, (state) => {
        // Only show loading on first track (no existing data)
        if (!state.trackingData) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(trackDelivery.fulfilled, (state, action) => {
        state.loading = false;
        state.trackingData = action.payload;
      })
      .addCase(trackDelivery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addMatcher(
        (action) => ['auth/logout', 'auth/logoutUser/fulfilled', 'auth/logoutUser/rejected'].includes(action.type),
        (state) => {
          state.deliveries = [];
          state.ordersRequestId = null;
          state.ordersLoading = false;
          state.ordersError = null;
          state.pickupLocation = null;
          state.dropLocation = null;
          state.selectedVehicle = null;
          state.senderContact = null;
          state.receiverContact = null;
          state.parcelDetails = {
            parcelSize: '',
            parcelWeight: '',
            parcelDescription: '',
          };
          state.estimatedCost = 0;
          state.currentDelivery = null;
          state.trackingData = null;
          state.error = null;
        }
      );
  },
});

export const {
  setPickupLocation,
  setDropLocation,
  setSelectedVehicle,
  setSenderContact,
  setReceiverContact,
  setParcelDetails,
  setEstimatedCost,
  resetDeliveryForm,
  clearError,
} = deliverySlice.actions;

export default deliverySlice.reducer;
